import React, { Suspense, useState } from 'react';

import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import { Track } from '../../../weborder/analytics';
import { Snackbar, Typography } from '../../atoms';
import { Dialog, LoadingSpinner } from '../../layouts';
import { PaymentMethodList } from './partials';
import { paymentMethodsUtils } from './utils';

const DeleteCardConfirmDialog = React.lazy(
  () => import('./partials/DeleteCardConfirmDialog'),
);

const useStyles = makeStyles((theme: Theme) => ({
  paymentMethodsDialog: {
    '& .MuiDialogContent-root': {
      minHeight: '380px',
    },
  },
  title: {
    paddingBottom: theme.spacing(2),
  },
}));

export type Props = {
  addCard: () => void;
  deletePaymentMethod: (p: { paymentId: string }) => void;
  payments: WebOrder.State.Payment[];
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  showDialog: boolean;
  translate: any;
};

export function PaymentMethodsDialog(props: Props): JSX.Element {
  const {
    addCard,
    deletePaymentMethod,
    payments,
    setShowDialog,
    showDialog,
    translate,
  } = props;

  const classes = useStyles({});

  const [error, setError] = useState('');
  const [cardToDelete, setCardToDelete] = useState(null);

  const handleCloseDialog = (): void => {
    setShowDialog(false);
  };

  const handleOnAddCard = (): void => {
    Track('Payments', 'Add New Card Begin');
    addCard();
  };

  const handleOnDelete = async (paymentAccountId: number): Promise<void> => {
    setCardToDelete(null);
    const errorMessage = await paymentMethodsUtils.handleDelete(
      paymentAccountId,
      translate,
    );

    if (errorMessage) {
      setError(errorMessage);
    } else {
      deletePaymentMethod({ paymentId: `${paymentAccountId}` });
      Track('Payments', 'Remove Card');
    }
  };

  const showDeleteConfirmationDialog = (
    cardText: string,
    paymentId: number,
  ) => {
    setCardToDelete({ cardText, paymentId });
  };

  return (
    <Dialog
      className={classes.paymentMethodsDialog}
      dialogName="payment-methods"
      onTitleClick={handleCloseDialog}
      open={showDialog}
    >
      <header className="fd-header">
        <Typography className={classes.title} variant="h1">
          {translate('Payment_methods_title')}
        </Typography>
      </header>

      <article className="fd-content__main">
        <PaymentMethodList
          onAddCard={handleOnAddCard}
          onDelete={showDeleteConfirmationDialog}
          payments={payments}
          translate={translate}
        />

        {!!error && <Snackbar message={error} variant="error" />}
      </article>
      {cardToDelete !== null && (
        <Suspense fallback={<LoadingSpinner />}>
          <DeleteCardConfirmDialog
            card={cardToDelete}
            onCloseDeleteConfirm={() => setCardToDelete(null)}
            onDeleteConfirm={handleOnDelete}
            open={cardToDelete !== null}
            translate={translate}
          />
        </Suspense>
      )}
    </Dialog>
  );
}
